import { useNavigate } from "react-router-dom";
import folderService from "../services/folderServices";
import requests from "../services/httpServices";
import React, { useState, useContext, createContext, useCallback } from "react";
import { toast } from "react-toastify";
import axios from "axios";

const MeetingContext = createContext(undefined);

export function useMeetingContext() {
  const ctx = useContext(MeetingContext);
  if (!ctx)
    throw new Error("useMeetingContext must be used within MeetingProvider");
  return ctx;
}

export function MeetingProvider({ children }) {
  // Existing state for widget functionality
  const [showPanel, setShowPanel] = useState(false);
  const [showWidgetPanel, setShowWidgetPanel] = useState(false);
  const [folders, setFolders] = useState([]);
  const [name, setName] = useState("");
  const [mediaFileName, setMediaFileName] = useState("");
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [step, setStep] = useState("create_element");
  const [viewCtaPreset, setViewCtaPreset] = useState(null);
  const [viewVideoPreset, setViewVideoPreset] = useState(null);
  const [viewDocumentPreset, setViewDocumentPreset] = useState(null);
  const [viewWebPagePreset, setViewWebPagePreset] = useState(null);
  const [selectedCreateElement, setSelectedCreateElement] = useState("");
  const [updateFolderId, setUpdateFolderId] = useState("");
  const [updatePresetId, setUpdatePresetId] = useState("");
  const [websiteUpdateData, setWebsiteUpdateData] = useState(null);
  const [ctaUpdateData, setCtaUpdateData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const getFolders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await folderService.getFolders();

      if (response) {
        setFolders(response);
      }
    } catch (err) {
      setError(err.message || "Failed to load folders");
    } finally {
      setLoading(false);
    }
  }, []);

  const navigate = useNavigate();

  const createFolder = async (name) => {
    await requests.post("/element/folder", { name });
    await getFolders();
  };

  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await requests.post("/images/upload", formData);

      return response.data;
    } catch (error) {
      console.error(error);
      throw new Error("Image upload failed");
    }
  };

  const createWebPagePreset = async (data) => {
    try {
      await requests.post("/element/create-web-page-preset", data);
      setStep("create_element");
      await getFolders();
      setSelectedCreateElement("");
      toast.success("Webpage created");
      navigate(`/dashboard/folders/element/${data.folderId}`, {
        replace: true,
      });
    } catch (error) {
      toast.error("Failed to create");
    }
  };

  const createPreset = async (data) => {
    try {
      await requests.post("/element/cta-preset", {
        ...data,
        id: updatePresetId,
      });
    } catch (error) {
      console.error(error);
      toast.error(`Error creating preset ${getNetworkErrorMessage(error)}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        // transition: Bounce,
      });
      return;
    }

    setStep("create_element");
    await getFolders();
    setUpdatePresetId("");
    setCtaUpdateData(null);
    navigate(`/dashboard/folders/${data.folderId}`, { replace: true });
    // setSelectedSubFolder(null);
  };

  const createMediaFile = async (data) => {
    setLoading(true);
    setUploadProgress(0); // Reset upload progress
    const formData = new FormData();
    // formData.append("file", data.file); // Append the actual file object
    formData.append("folderId", data.folderId);
    formData.append("name", data.name);
    formData.append("type", data.type);
    if (data.id) {
      formData.append("id", data.id);
    }

    try {
      console.log("data", data.file);
      const presignResponse = await requests.post("/element/pre-signed-url", {
        name: data.file.name,
        type: data.file.type
      });
      formData.append("url", presignResponse.url.key);
      await axios.put(presignResponse.url.url, data.file, {
        headers: { "Content-Type": data.file.type },
        onUploadProgress: (progressEvent) => {
          const percent = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percent);
        },
      });

      console.log("presignResponse", presignResponse);
      await requests.post("/element/create-slide", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        // onUploadProgress: (progressEvent) => {
        //   const percent = Math.round(
        //     (progressEvent.loaded * 100) / progressEvent.total
        //   );
        //   setUploadProgress(percent);
        // },
      });
      setUploadProgress(0);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      toast.error(`Error creating preset ${getNetworkErrorMessage(error)}`);
      setUploadProgress(0);
      return;
    }

    setStep("create_element");
    setSelectedCreateElement("");
    setUpdatePresetId("");
    await getFolders();
    toast.success("Element Created");
    navigate(`/dashboard/folders/element/${data.folderId}`, { replace: true });
  };

  const validateUsername = (username) => {
    if (!username.trim()) {
      return "Username is required";
    }
    if (username.length < 2) {
      return "Username must be at least 2 characters";
    }
    return null;
  };

  const onClosePanel = () => {
    if (selectedCreateElement === "website") {
      setSelectedCreateElement("");
      setViewWebPagePreset(null);
      setViewDocumentPreset(null);
      setViewVideoPreset(null);
    } else if (viewDocumentPreset) {
      setSelectedCreateElement("");
      setViewWebPagePreset(null);
      setViewDocumentPreset(null);
      setViewVideoPreset(null);
    } else if (step === "create_element") {
      setSelectedCreateElement("");
    } else {
      setShowPanel(false);
      setShowWidgetPanel(false);
    }
  };

  const getNetworkErrorMessage = (error) => {
    if (error.response) {
      const response = error.response;
      console.log("debut123123", response);
      if (response.data.errors) {
        return `Error: ${response.data.errors.map((err) => err).join(", ")}`;
      } else {
        return "Data is not valid";
      }
    }

    return "Try again, something went wrong";
  };

  const contextValue = {
    // Existing widget state
    showPanel,
    setShowPanel,
    showWidgetPanel,
    setShowWidgetPanel,
    folders,
    setFolders,
    createFolder,
    name,
    setName,
    mediaFileName,
    setMediaFileName,
    createPreset,
    createMediaFile,
    selectedFolder,
    setSelectedFolder,
    step,
    setStep,
    viewCtaPreset,
    setViewCtaPreset,
    uploadImage,
    viewVideoPreset,
    setViewVideoPreset,

    validateUsername,
    getFolders,
    selectedCreateElement,
    setSelectedCreateElement,
    createWebPagePreset,
    setViewWebPagePreset,
    viewWebPagePreset,

    onClosePanel,
    setViewDocumentPreset,
    viewDocumentPreset,
    setUpdateFolderId,
    updateFolderId,

    setUpdatePresetId,
    updatePresetId,
    websiteUpdateData,
    setWebsiteUpdateData,
    ctaUpdateData,
    setCtaUpdateData,
    loading,
    setLoading,
    error,
    setError,
    uploadProgress,
  };

  return (
    <MeetingContext.Provider value={contextValue}>
      {children}
    </MeetingContext.Provider>
  );
}
